import { Locale } from "./locale";

export function getLocalizedShortenedNumber(value: number, lang: Locale) {
  // For Asian languages (Japanese, Korean, Chinese)
  const isAsianLanguage = ["ja", "ko", "zh", "zh-hans"].includes(lang);

  const formatNumber = (num: number) => {
    return (num % 1 === 0 ? num : parseFloat(num.toFixed(2))).toString();
  };

  if (isAsianLanguage) {
    // Asian number system: 万 (10K), 千万 (10M), 億 (100M), 兆 (1T)
    const units = {
      ja: { man: "万", senman: "千万", oku: "億", chou: "兆" },
      ko: { man: "만", senman: "천만", oku: "억", chou: "조" },
      zh: { man: "萬", senman: "千萬", oku: "億", chou: "兆" },
      "zh-hans": { man: "万", senman: "千万", oku: "亿", chou: "万亿" },
    };

    const langUnits = units[lang as keyof typeof units] || units["zh-hans"];

    if (value >= 1_000_000_000_000) {
      // Trillion (兆 or 万亿)
      const result = value / 1_000_000_000_000;
      return formatNumber(result) + langUnits.chou;
    } else if (value >= 100_000_000) {
      // Hundred million (億)
      const result = value / 100_000_000;
      return formatNumber(result) + langUnits.oku;
    } else if (value >= 10_000_000) {
      // Ten million (千万)
      const result = value / 10_000_000;
      return formatNumber(result) + langUnits.senman;
    } else if (value >= 10_000) {
      // Ten thousand (万)
      const result = value / 10_000;
      return formatNumber(result) + langUnits.man;
    }
    return value.toString();
  }

  // Western languages - each has different abbreviations and spacing
  const getUnits = () => {
    switch (lang) {
      case "fr":
        return { T: " T", B: " MRD", M: " M", K: " K" };
      case "de":
        return { T: " Bio.", B: " Mrd.", M: " Mio.", K: " Tsd." };
      case "it":
        return { T: " BLN", B: " MLD", M: " MLN", K: " K" };
      case "pl":
        return { T: " bln", B: " mld", M: " mln", K: " tys" };
      case "pt":
        return { T: " Tri", B: " Bi", M: " Mi", K: " Mil" };
      case "ru":
        return { T: " трлн", B: " млрд", M: " млн", K: " тыс" };
      case "es":
        return { T: " B", B: " MM", M: " M", K: " K" };
      case "tr":
        return { T: " trilyon", B: " milyar", M: " milyon", K: " bin" };
      default: // en
        return { T: "T", B: "B", M: "M", K: "K" };
    }
  };

  const units = getUnits();

  if (value >= 1_000_000_000_000) {
    // Trillion
    const result = value / 1_000_000_000_000;
    return formatNumber(result) + units.T;
  } else if (value >= 1_000_000_000) {
    // Billion
    const result = value / 1_000_000_000;
    return formatNumber(result) + units.B;
  } else if (value >= 1_000_000) {
    // Million
    const result = value / 1_000_000;
    return formatNumber(result) + units.M;
  } else if (value >= 1_000) {
    // Thousand
    const result = value / 1_000;
    return formatNumber(result) + units.K;
  }

  return value.toString();
}
